"use strict";

exports.__esModule = true;
exports.RegionAttachment = void 0;

var _Utils = require("../Utils");

var _Attachment2 = require("./Attachment");

function _inheritsLoose(subClass, superClass) { subClass.prototype = Object.create(superClass.prototype); subClass.prototype.constructor = subClass; _setPrototypeOf(subClass, superClass); }

function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }

/** An attachment that displays a textured quadrilateral.
 *
 * See [Region attachments](http://esotericsoftware.com/spine-regions) in the Spine User Guide. */
var RegionAttachment = /*#__PURE__*/function (_Attachment) {
  _inheritsLoose(RegionAttachment, _Attachment);

  function RegionAttachment(name) {
    var _this;

    _this = _Attachment.call(this, name) || this;
    /** The local x translation. */

    _this.x = 0;
    /** The local y translation. */

    _this.y = 0;
    /** The local scaleX. */

    _this.scaleX = 1;
    /** The local scaleY. */

    _this.scaleY = 1;
    /** The local rotation. */

    _this.rotation = 0;
    /** The width of the region attachment in Spine. */

    _this.width = 0;
    /** The height of the region attachment in Spine. */

    _this.height = 0;
    /** The color to tint the region attachment. */

    _this.color = new _Utils.Color(1, 1, 1, 1);
    /** The name of the texture region for this attachment. */

    _this.path = null;
    _this.rendererObject = null;
    _this.region = null;
    /** For each of the 4 vertices, a pair of <code>x,y</code> values that is the local position of the vertex.
     *
     * See {@link #updateOffset()}. */

    _this.offset = _Utils.Utils.newFloatArray(8);
    _this.uvs = _Utils.Utils.newFloatArray(8);
    _this.tempColor = new _Utils.Color(1, 1, 1, 1);
    return _this;
  }
  /** Calculates the {@link #offset} using the region settings. Must be called after changing region settings. */


  var _proto = RegionAttachment.prototype;

  _proto.updateOffset = function updateOffset() {
    var region = this.region;
    var regionScaleX = this.width / this.region.originalWidth * this.scaleX;
    var regionScaleY = this.height / this.region.originalHeight * this.scaleY;
    var localX = -this.width / 2 * this.scaleX + this.region.offsetX * regionScaleX;
    var localY = -this.height / 2 * this.scaleY + this.region.offsetY * regionScaleY;
    var localX2 = localX + this.region.width * regionScaleX;
    var localY2 = localY + this.region.height * regionScaleY;
    var radians = this.rotation * Math.PI / 180;
    var cos = Math.cos(radians);
    var sin = Math.sin(radians);
    var x = this.x,
        y = this.y;
    var localXCos = localX * cos + x;
    var localXSin = localX * sin;
    var localYCos = localY * cos + y;
    var localYSin = localY * sin;
    var localX2Cos = localX2 * cos + x;
    var localX2Sin = localX2 * sin;
    var localY2Cos = localY2 * cos + y;
    var localY2Sin = localY2 * sin;
    var offset = this.offset;
    offset[0] = localXCos - localYSin;
    offset[1] = localYCos + localXSin;
    offset[2] = localXCos - localY2Sin;
    offset[3] = localY2Cos + localXSin;
    offset[4] = localX2Cos - localY2Sin;
    offset[5] = localY2Cos + localX2Sin;
    offset[6] = localX2Cos - localYSin;
    offset[7] = localYCos + localX2Sin;
  };

  _proto.setRegion = function setRegion(region) {
    this.region = region;
    var uvs = this.uvs;

    if (region.degrees == 90) {
      uvs[2] = region.u;
      uvs[3] = region.v2;
      uvs[4] = region.u;
      uvs[5] = region.v;
      uvs[6] = region.u2;
      uvs[7] = region.v;
      uvs[0] = region.u2;
      uvs[1] = region.v2;
    } else {
      uvs[0] = region.u;
      uvs[1] = region.v2;
      uvs[2] = region.u;
      uvs[3] = region.v;
      uvs[4] = region.u2;
      uvs[5] = region.v;
      uvs[6] = region.u2;
      uvs[7] = region.v2;
    }
  }
  /** Transforms the attachment's four vertices to world coordinates.
   *
   * See [World transforms](http://esotericsoftware.com/spine-runtime-skeletons#World-transforms) in the Spine
   * Runtimes Guide.
   * @param worldVertices The output world vertices. Must have a length >= `offset` + 8.
   * @param offset The `worldVertices` index to begin writing values.
   * @param stride The number of `worldVertices` entries between the value pairs written. */
  ;

  _proto.computeWorldVertices = function computeWorldVertices(bone, worldVertices, offset, stride) {
    var vertexOffset = this.offset;
    var x = bone.worldX,
        y = bone.worldY;
    var a = bone.a,
        b = bone.b,
        c = bone.c,
        d = bone.d;
    var offsetX = 0,
        offsetY = 0;
    offsetX = vertexOffset[0];
    offsetY = vertexOffset[1];
    worldVertices[offset] = offsetX * a + offsetY * b + x; // br

    worldVertices[offset + 1] = offsetX * c + offsetY * d + y;
    offset += stride;
    offsetX = vertexOffset[2];
    offsetY = vertexOffset[3];
    worldVertices[offset] = offsetX * a + offsetY * b + x; // bl

    worldVertices[offset + 1] = offsetX * c + offsetY * d + y;
    offset += stride;
    offsetX = vertexOffset[4];
    offsetY = vertexOffset[5];
    worldVertices[offset] = offsetX * a + offsetY * b + x; // ul

    worldVertices[offset + 1] = offsetX * c + offsetY * d + y;
    offset += stride;
    offsetX = vertexOffset[6];
    offsetY = vertexOffset[7];
    worldVertices[offset] = offsetX * a + offsetY * b + x; // ur

    worldVertices[offset + 1] = offsetX * c + offsetY * d + y;
  };

  _proto.copy = function copy() {
    var copy = new RegionAttachment(this.name);
    copy.region = this.region;
    copy.rendererObject = this.rendererObject;
    copy.path = this.path;
    copy.x = this.x;
    copy.y = this.y;
    copy.scaleX = this.scaleX;
    copy.scaleY = this.scaleY;
    copy.rotation = this.rotation;
    copy.width = this.width;
    copy.height = this.height;

    _Utils.Utils.arrayCopy(this.uvs, 0, copy.uvs, 0, 8);

    _Utils.Utils.arrayCopy(this.offset, 0, copy.offset, 0, 8);

    copy.color.setFromColor(this.color);
    return copy;
  };

  return RegionAttachment;
}(_Attachment2.Attachment);

exports.RegionAttachment = RegionAttachment;
RegionAttachment.X1 = 0;
RegionAttachment.Y1 = 1;
RegionAttachment.C1R = 2;
RegionAttachment.C1G = 3;
RegionAttachment.C1B = 4;
RegionAttachment.C1A = 5;
RegionAttachment.U1 = 6;
RegionAttachment.V1 = 7;
RegionAttachment.X2 = 8;
RegionAttachment.Y2 = 9;
RegionAttachment.C2R = 10;
RegionAttachment.C2G = 11;
RegionAttachment.C2B = 12;
RegionAttachment.C2A = 13;
RegionAttachment.U2 = 14;
RegionAttachment.V2 = 15;
RegionAttachment.X3 = 16;
RegionAttachment.Y3 = 17;
RegionAttachment.C3R = 18;
RegionAttachment.C3G = 19;
RegionAttachment.C3B = 20;
RegionAttachment.C3A = 21;
RegionAttachment.U3 = 22;
RegionAttachment.V3 = 23;
RegionAttachment.X4 = 24;
RegionAttachment.Y4 = 25;
RegionAttachment.C4R = 26;
RegionAttachment.C4G = 27;
RegionAttachment.C4B = 28;
RegionAttachment.C4A = 29;
RegionAttachment.U4 = 30;
RegionAttachment.V4 = 31;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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